import { useMemo } from 'react'
import {
  ONBOARDING_CALENDAR_SYNC_SUPPORTED_INTEGRATIONS,
  getIntegrationServiceIdsForCategory,
  IntegrationCategory,
  ONBOARDING_UNSUPPORTED_INTEGRATIONS,
} from '../../common/integrations'
import { type CapabilityState } from '../../models/Onboarding'
import { selectAccountId } from '../auth/authSlice'
import { useAppSelector } from '../hooks'
import { useGetMeQuery } from '../user/user'
import { useIntegrations } from '../../views/Integration/useIntegrations'
import { useCalendarSyncStates } from '../../views/Integration/IntegrationManagement/OnboardingSupport/useCalendarSyncStates'

const PRIMARY_CALENDAR_SOURCES = getIntegrationServiceIdsForCategory(IntegrationCategory.CALENDAR).filter(
  (id) => !ONBOARDING_UNSUPPORTED_INTEGRATIONS.includes(id),
)

const SECONDARY_CALENDAR_SOURCES = [...ONBOARDING_CALENDAR_SYNC_SUPPORTED_INTEGRATIONS].filter((id) => !ONBOARDING_UNSUPPORTED_INTEGRATIONS.includes(id))

const ALL_CALENDAR_SOURCES = [...PRIMARY_CALENDAR_SOURCES, ...SECONDARY_CALENDAR_SOURCES]

export const useCalendarCapability = (): CapabilityState & { isLoading: boolean } => {
  const accountId = useAppSelector(selectAccountId)
  const { data: me, isLoading: isMeLoading } = useGetMeQuery({ accountId })
  const { isIntegrationAllowed, isIntegrationSyncDisabled, isLoading: isIntegrationsLoading } = useIntegrations()
  const { calendarSyncActive, isLoading: isSyncLoading } = useCalendarSyncStates()

  const connectedIds = useMemo(() => new Set(me?.integrations?.map((i) => i.serviceId) ?? []), [me])

  return useMemo<CapabilityState & { isLoading: boolean }>(() => {
    const allowedServiceIds = PRIMARY_CALENDAR_SOURCES.filter((id) => isIntegrationAllowed(id) && !isIntegrationSyncDisabled(id))
    const allowedSecondaryIds = SECONDARY_CALENDAR_SOURCES.filter((id) => isIntegrationAllowed(id) && !isIntegrationSyncDisabled(id))
    const isAllowed = allowedServiceIds.length > 0 || allowedSecondaryIds.length > 0
    const isFulfilled = calendarSyncActive.size > 0
    const connectedIntegrations = ALL_CALENDAR_SOURCES.filter((id) => connectedIds.has(id))
    const connectedWithoutSync = ALL_CALENDAR_SOURCES.filter((id) => connectedIds.has(id) && !calendarSyncActive.has(id))
    return {
      isAllowed,
      isFulfilled,
      allowedServiceIds,
      allowedSecondaryIds,
      connectedIntegrations,
      connectedWithoutSync,
      isLoading: isMeLoading || isIntegrationsLoading || isSyncLoading,
    }
  }, [isIntegrationAllowed, isIntegrationSyncDisabled, connectedIds, calendarSyncActive, isMeLoading, isIntegrationsLoading, isSyncLoading])
}
